import classNames from 'classnames';
import { FiExternalLink } from 'react-icons/fi';
import { BsThreeDotsVertical } from 'react-icons/bs';
import React, { useEffect, useMemo, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { isObjectEmpty } from 'utils/object';
import { handleError } from 'utils/errorHandler';
import { getFormattedDate } from 'utils/date';
import { interpolate, pluralize, withoutProtocolAndSubDomainURL } from 'utils/string';

import * as routes from 'constants/routes';
import * as permissions from 'constants/permissions';
import {
  DATE_FORMAT_FOR_DISPLAY,
  EVENTS_REFERENCE_PERIOD,
  EVENTS_REFERENCE_PERIOD_PILL_TABS,
} from 'constants/appConstants';
import { REMINDER_TITLE_LENGTH, TOOLTIP_LIMIT_LENGTH } from 'constants/string';

import Table from 'components/common/table';
import PopOver from 'components/common/popover';
import PillTab from 'components/common/pillTab';
import EditDelete from 'components/common/editDelete';
import TableHeader from 'components/common/table/TableHeader';

import * as reminderService from 'services/reminder';

import { useDeleteReminderMutation, useUserPermissions } from 'hooks/query/useUserInfo';
import useQuery from 'hooks/useQuery';
import useTitle from 'hooks/useTitle';
import useCurrentFiscalYear from 'hooks/useCurrentFiscalYear';

type ReminderQuery = {
  type?: string;
};

type ReminderRecord = {
  id: number;
  title: string;
  description: string;
  redirectUrl: string;
  startDate: string;
  endDate: string;
};

type TableRow = {
  original: ReminderRecord;
  index?: number;
  viewIndex?: number;
};

type PopperData = {
  styles: Record<string, string | number>;
};

const ReminderList: React.FC<RouteComponentProps> = (props) => {
  useTitle('Reminders');

  const [query, setQuery] = useQuery(props as never) as [ReminderQuery, (query: ReminderQuery) => void];

  const [isDataLoading, setIsDataLoading] = useState(true);
  const [reminderList, setReminderList] = useState<ReminderRecord[]>([]);
  const [columnToSortBy, setColumnToSortBy] = useState('title');
  const [isAscendingOrdered, setIsAscendingOrdered] = useState(false);
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);
  const deleteReminderMutation = useDeleteReminderMutation();
  const userPermissions = useUserPermissions();

  const { type } = query;

  const currentFiscalYear = useCurrentFiscalYear() as Record<string, string>;

  const createPopperOptions = (customStyles: Record<string, string | number>) => {
    return {
      distance: 5,
      popperOptions: {
        modifiers: {
          flip: {
            enabled: false,
          },
          addZIndex: {
            enabled: true,
            order: 812,
            fn: (data: PopperData) => {
              return {
                ...data,
                styles: {
                  ...data.styles,
                  ...customStyles,
                },
              };
            },
          },
        },
      },
    };
  };

  const fetchReminders = async (queryType: string) => {
    setIsDataLoading(true);

    try {
      const { data } = (await reminderService.fetchAll({ type: queryType })) as { data: ReminderRecord[] };

      const formattedData = data.map((dataItem) => {
        const description = dataItem.description || '-';
        const redirectUrl = dataItem.redirectUrl || '-';
        const startDate = getFormattedDate(dataItem.startDate);
        const endDate = getFormattedDate(dataItem.endDate);

        return {
          ...dataItem,
          description,
          redirectUrl,
          startDate,
          endDate,
        };
      });

      setReminderList(formattedData);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleEdit = (row: TableRow) => {
    history.push(interpolate(routes.UPDATE_REMINDER, { id: row.original.id }));
  };

  const handleDelete = (row: TableRow) => {
    deleteReminderMutation.mutate(row.original.id, {
      onSuccess: () => {
        toast.success({
          title: 'Success',
          message: 'Reminder deleted successfully.',
        });

        fetchReminders(type);
      },
    });
  };

  useEffect(() => {
    if (isObjectEmpty(currentFiscalYear) || type) {
      return;
    }

    setQuery({ type: EVENTS_REFERENCE_PERIOD.UPCOMING.toLowerCase() });
  }, [currentFiscalYear, type, setQuery]);

  useEffect(() => {
    if (isObjectEmpty(currentFiscalYear) || !type) {
      return;
    }

    const loadReminders = async () => {
      setIsDataLoading(true);

      try {
        const { data } = (await reminderService.fetchAll({ type })) as { data: ReminderRecord[] };

        const formattedData = data.map((dataItem) => {
          const description = dataItem.description || '-';
          const redirectUrl = dataItem.redirectUrl || '-';
          const startDate = getFormattedDate(dataItem.startDate);
          const endDate = getFormattedDate(dataItem.endDate);

          return {
            ...dataItem,
            description,
            redirectUrl,
            startDate,
            endDate,
          };
        });

        setReminderList(formattedData);
      } catch (error) {
        handleError(error);
      } finally {
        setIsDataLoading(false);
      }
    };

    void loadReminders();
  }, [currentFiscalYear, type]);

  const getRowProps = (_state: object, rowInfo: TableRow) => {
    return {
      className: 'align-items-center d-flex',
      onClick: (e: React.MouseEvent) => {
        if (!userPermissions[permissions.REMINDERS.UPDATE]) {
          return;
        }

        const url = interpolate(routes.UPDATE_REMINDER, { id: rowInfo.original.id });

        if (e.ctrlKey || e.metaKey) {
          window.open(url);
        } else {
          history.push(url);
        }
      },
    };
  };

  const getLinkCell = () => {
    return {
      Header: <TableHeader label="Link" identifier="redirectUrl" />,
      accessor: 'redirectUrl',
      sortable: false,
      filterable: false,
      maxWidth: 300,
      Cell: (cell: TableRow) => {
        const isNotFirstRow = cell.index !== 0;
        const linkExceedsLimit = cell.original.redirectUrl.length > TOOLTIP_LIMIT_LENGTH;
        const top = linkExceedsLimit ? -40 : -20;
        const moreProps = isNotFirstRow ? {} : createPopperOptions({ zIndex: 10, top });
        return cell.original.redirectUrl !== '-' ? (
          <PopOver
            interactive
            html={
              <div className="tooltip-info word-break">
                <p className="tooltip-info__title">{cell.original.redirectUrl}</p>
              </div>
            }
            position="top-start"
            visible={Boolean(withoutProtocolAndSubDomainURL(cell.original.redirectUrl).length)}
            {...moreProps}
          >
            <a
              href={cell.original.redirectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex align-items-center table-link"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="table-link__text ellipsis">
                {withoutProtocolAndSubDomainURL(cell.original.redirectUrl)}
              </div>
              <div className="center">
                <FiExternalLink className="table-link__icon" size={12} />
              </div>
            </a>
          </PopOver>
        ) : (
          <>{cell.original.redirectUrl}</>
        );
      },
    };
  };

  const getDeleteCell = () => {
    const canEdit = Boolean(userPermissions[permissions.REMINDERS.UPDATE]);
    const canDelete = Boolean(userPermissions[permissions.REMINDERS.DELETE]);
    const canEditOrDelete = canEdit || canDelete;

    return {
      Header: '',
      width: 6,
      className: 'margin-left-auto cursor-default',
      Cell: (row: TableRow) =>
        canEditOrDelete ? (
          <span
            className="verticle-ellipse cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <PopOver
              interactive
              open={openPopoverId === row.original.id}
              onRequestClose={() => setOpenPopoverId(null)}
              theme="light"
              size="regular"
              customClass="overflow-menu__regular"
              position="left-start"
              disableOnScroll={true}
              popperOptions={{
                modifiers: {
                  addZIndex: {
                    enabled: true,
                    order: 810,
                    fn: (data: PopperData) => ({
                      ...data,
                      styles: {
                        ...data.styles,
                        zIndex: 10,
                      },
                    }),
                  },
                },
              }}
              html={
                <EditDelete
                  onClose={() => setOpenPopoverId(null)}
                  onDelete={() => handleDelete(row)}
                  onEdit={() => handleEdit(row)}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  type="reminder"
                  title={row.original.title}
                />
              }
            >
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenPopoverId(openPopoverId === row.original.id ? null : row.original.id);
                }}
              >
                <BsThreeDotsVertical size={20} />
              </span>
            </PopOver>
          </span>
        ) : null,
    };
  };

  const columns = useMemo(
    () => [
      {
        Header: <TableHeader label="SN." identifier="sn" />,
        accessor: 'sn',
        maxWidth: 56,
        sortable: false,
        filterable: false,
        Cell: (row: TableRow) => {
          const index = (row.viewIndex || 0) + 1;
          return <span className="capital-text employees__table-text">{index < 10 ? `0${index}` : index}</span>;
        },
      },
      {
        Header: (
          <TableHeader
            label="Title"
            identifier="title"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'title') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('title');
            }}
          />
        ),
        accessor: 'title',
        maxWidth: 700,
        Cell: (cell: TableRow) => {
          const isNotFirstRow = cell.index !== 0;
          const moreProps = isNotFirstRow ? {} : createPopperOptions({ zIndex: 10, top: -40 });
          return (
            <PopOver
              interactive
              html={
                <div className="tooltip-info word-break">
                  <p className="tooltip-info__title">{cell.original.title}</p>
                </div>
              }
              position="top-start"
              visible={cell.original.title.length > REMINDER_TITLE_LENGTH}
              {...moreProps}
            >
              <div className="ellipsis">{cell.original.title}</div>
            </PopOver>
          );
        },
      },
      {
        Header: (
          <TableHeader
            label="Start Date"
            identifier="startDate"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'startDate') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('startDate');
            }}
          />
        ),
        accessor: 'startDate',
        maxWidth: 180,
        Cell: (cell: TableRow) => <span>{getFormattedDate(cell.original.startDate, DATE_FORMAT_FOR_DISPLAY)}</span>,
      },
      {
        Header: (
          <TableHeader
            label="End Date"
            identifier="endDate"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'endDate') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('endDate');
            }}
          />
        ),
        accessor: 'endDate',
        maxWidth: 180,
        Cell: (cell: TableRow) => <span>{getFormattedDate(cell.original.endDate, DATE_FORMAT_FOR_DISPLAY)}</span>,
      },
    ],
    [columnToSortBy, isAscendingOrdered]
  );

  const onToggleChange = async (newValue: ReminderQuery) => {
    setQuery({ ...newValue });
  };

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div>
          <div className="table-title">Reminders</div>
          <div className="table-subtitle">
            Showing {reminderList.length} {pluralize('Result', reminderList.length)}
          </div>
        </div>
        <div className="table-header__right">
          <PillTab
            tabs={EVENTS_REFERENCE_PERIOD_PILL_TABS}
            onTabClick={(value) => onToggleChange({ type: String(value) })}
            isActive={(value) => String(value).toLowerCase() === query?.type?.toLowerCase()}
          />
        </div>
      </div>
      <div className={classNames('full-scope-card__content')}>
        <Table
          loading={isDataLoading}
          data={reminderList}
          columns={[...columns, getLinkCell(), getDeleteCell()]}
          showPagination={false}
          noDataText="No reminders to show."
          getTrProps={getRowProps}
        />
      </div>
    </div>
  );
};

export default ReminderList;
